import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Link,
  useToast,
  Container,
  Flex,
  useColorMode,
  Image,
  Divider,
  useColorModeValue,
  Heading,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useTranslation } from "react-i18next";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
import Turnstile from "react-turnstile";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const isDevelopment = process.env.REACT_APP_ENV === "development";

export const Register: React.FC = () => {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [referralCode, setReferralCode] = React.useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState<string>("");
  const { register, isLoading, error } = useAuthStore();
  const toast = useToast();
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  // Load referral code on mount
  React.useEffect(() => {
    const code = sessionStorage.getItem("referralCode");
    if (code) {
      setReferralCode(code);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isDevelopment && !turnstileToken) {
      toast({
        title: t("auth.register.error"),
        description: t("auth.register.pleaseVerify"),
        status: "error",
        duration: 5000,
      });
      return;
    }

    try {
      await register(
        email,
        password,
        firstName,
        lastName,
        referralCode || undefined,
        turnstileToken || "dev-mode"
      );
      sessionStorage.removeItem("referralCode");
      toast({
        title: t("auth.register.success"),
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      toast({
        title: t("auth.register.error"),
        description: error || t("auth.register.errorMessage"),
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PublicNavbar />
      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl" mt={20}>
          <VStack spacing={8}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("auth.register.title")}
              </Heading>
            </VStack>

            <Flex
              direction={{ base: "column", md: "row" }}
              gap={8}
              w="100%"
              align="stretch"
            >
              {/* Social Login Section */}
              <Box
                flex="1"
                p={8}
                borderWidth={1}
                borderRadius="lg"
                boxShadow="lg"
                bg={colorMode === "dark" ? "gray.800" : "white"}
              >
                <VStack spacing={6}>
                  <Text fontSize="xl" fontWeight="bold" mb={4}>
                    {t("auth.register.socialTitle")}
                  </Text>

                  <Button
                    w="full"
                    size="lg"
                    variant="outline"
                    leftIcon={
                      <Image
                        src="/assets/img/google.svg"
                        alt="Google"
                        boxSize="20px"
                      />
                    }
                    onClick={() =>
                      (window.location.href = `${API_URL}/auth/google${
                        referralCode ? `?referralCode=${referralCode}` : ""
                      }`)
                    }
                  >
                    {t("auth.login.googleButton")}
                  </Button>

                  <Button
                    w="full"
                    size="lg"
                    variant="outline"
                    leftIcon={
                      <Image
                        src="/assets/img/github.svg"
                        alt="GitHub"
                        boxSize="20px"
                      />
                    }
                    onClick={() =>
                      (window.location.href = `${API_URL}/auth/github${
                        referralCode ? `?referralCode=${referralCode}` : ""
                      }`)
                    }
                  >
                    {t("auth.login.githubButton")}
                  </Button>

                  <Button
                    w="full"
                    size="lg"
                    variant="outline"
                    leftIcon={
                      <Image
                        src="/assets/img/twitter.svg"
                        alt="Twitter"
                        boxSize="20px"
                      />
                    }
                    onClick={() =>
                      (window.location.href = `${API_URL}/auth/twitter${
                        referralCode ? `?referralCode=${referralCode}` : ""
                      }`)
                    }
                  >
                    {t("auth.login.twitterButton")}
                  </Button>

                  <Button
                    w="full"
                    size="lg"
                    variant="outline"
                    leftIcon={
                      <Image
                        src="/assets/img/facebook.svg"
                        alt="Facebook"
                        boxSize="20px"
                      />
                    }
                    onClick={() =>
                      (window.location.href = `${API_URL}/auth/facebook${
                        referralCode ? `?referralCode=${referralCode}` : ""
                      }`)
                    }
                  >
                    {t("auth.login.facebookButton")}
                  </Button>
                  {referralCode && (
                    <Box
                      p={3}
                      borderRadius="md"
                      bg={colorMode === "dark" ? "gray.700" : "gray.100"}
                      w="full"
                      textAlign="center"
                    >
                      <Text
                        fontSize="sm"
                        color={colorMode === "dark" ? "gray.300" : "gray.600"}
                      >
                        {t("auth.register.referralCode")}:
                      </Text>
                      <Text
                        fontWeight="bold"
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      >
                        {referralCode}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>

              {/* Regular Registration Form Section */}
              <Box
                flex="1"
                p={8}
                borderWidth={1}
                borderRadius="xl"
                bg={useColorModeValue("white", "gray.800")}
              >
                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>{t("auth.register.email")}</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>{t("auth.register.password")}</FormLabel>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>{t("auth.register.firstName")}</FormLabel>
                      <Input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>{t("auth.register.lastName")}</FormLabel>
                      <Input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </FormControl>

                    {!isDevelopment && (
                      <Box
                        w="full"
                        display="flex"
                        justifyContent="center"
                        my={4}
                      >
                        <Turnstile
                          sitekey={
                            process.env.REACT_APP_CLOUDFLARE_TURNSTILE || ""
                          }
                          onVerify={setTurnstileToken}
                          theme={colorMode}
                        />
                      </Box>
                    )}

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                    >
                      {t("auth.register.submit")}
                    </Button>
                  </VStack>
                </form>
              </Box>
            </Flex>
          </VStack>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};
